import { Injectable } from "@angular/core";
import { Camera, Cartesian3, Cartographic, Entity, JulianDate, Scene, SceneMode, ScreenSpaceCameraController, Viewer } from "cesium";
import { CesiumMapService } from "./cesium-map.service";
/* NOTE: CameraService is extracted from angular-cesium with updated types and minor optimization  */

@Injectable()
export class CameraService {
    private viewer: Viewer | undefined;
    private scene: Scene | undefined;
    private camera: Camera | undefined;
    private screenSpaceCameraController: ScreenSpaceCameraController | undefined;
    private lastRotate: boolean | undefined;
    private lastTilt: boolean | undefined;
    private lastLook: boolean | undefined;
    private isSceneModePerformance2D = false;

    public init(cesiumService: CesiumMapService) {
        this.viewer = cesiumService.getViewer();
        this.scene = cesiumService.getScene();
        this.screenSpaceCameraController = this.scene.screenSpaceCameraController;
        this.camera = this.scene.camera;
        this.lastRotate = this.screenSpaceCameraController.enableRotate;
        this.lastTilt = this.screenSpaceCameraController.enableTilt;
        this.lastLook = this.screenSpaceCameraController.enableLook;
    }

    private revertCameraProperties() {
        this.isSceneModePerformance2D = false;
        this.enableTilt(!!this.lastTilt);
        this.enableRotate(!!this.lastRotate);
        this.enableLook(!!this.lastLook);
    }

    public getCamera() {
        return this.camera;
    }

    public getScreenSpaceCameraController() {
        return this.screenSpaceCameraController;
    }

    public getMinimumZoom(): number {
        return this.screenSpaceCameraController?.minimumZoomDistance ?? 1;
    }

    public setMinimumZoom(amount: number): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.minimumZoomDistance = amount;
    }

    public getMaximumZoom(): number {
        return this.screenSpaceCameraController?.maximumZoomDistance ?? Infinity;
    }

    public setMaximumZoom(amount: number): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.maximumZoomDistance = amount;
    }

    public enableTilt(shouldAllowTilt: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableTilt = shouldAllowTilt;
    }

    public enableRotate(isRotatable: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableRotate = isRotatable;
    }

    public enableLook(shouldAllowLook: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableLook = shouldAllowLook;
    }

    public enableTranslate(shouldAllowTranslate: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableTranslate = shouldAllowTranslate;
    }

    public enableZoom(shouldAllowZoom: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableZoom = shouldAllowZoom;
    }

    public enableInputs(shouldAllowInputs: boolean): void {
        if (!this.screenSpaceCameraController) {
            return;
        }
        this.screenSpaceCameraController.enableInputs = shouldAllowInputs;
    }

    /**
     * Sets the map's SceneMode
     * @param sceneMode - The SceneMode to morph the scene into.
     * @param duration - The duration of scene morph animations, in seconds
     */
    public setSceneMode(sceneMode: SceneMode, duration?: number) {
        switch (sceneMode) {
            case SceneMode.SCENE3D: {
                if (this.isSceneModePerformance2D) {
                    this.revertCameraProperties();
                }

                this.scene?.morphTo3D(duration);

                break;
            }
            case SceneMode.COLUMBUS_VIEW: {
                if (this.isSceneModePerformance2D) {
                    this.revertCameraProperties();
                }

                this.scene?.morphToColumbusView(duration);

                break;
            }
            case SceneMode.SCENE2D: {
                if (this.isSceneModePerformance2D) {
                    this.revertCameraProperties();
                }
                this.scene?.morphTo2D(duration);

                break;
            }
            case SceneMode.MORPHING: {
                // Do nothing, as morphing is handled by other cases
                break;
            }
        }
    }

    public cameraFlyTo(options: Parameters<Camera["flyTo"]>[0]) {
        return this.camera?.flyTo(options);
    }

    public flyTo(target: Parameters<Viewer["flyTo"]>[0], options?: Parameters<Viewer["flyTo"]>[1]) {
        return this.viewer?.flyTo(target, options);
    }

    public zoomIn(amount: number) {
        return this.camera?.zoomIn(amount || this.camera.defaultZoomAmount);
    }

    public zoomOut(amount: number) {
        return this.camera?.zoomOut(amount || this.camera.defaultZoomAmount);
    }

    public zoomTo(target: Parameters<Viewer["zoomTo"]>[0], offset?: Parameters<Viewer["zoomTo"]>[1]) {
        return this.viewer?.zoomTo(target, offset);
    }

    public setView(options: Parameters<Camera["setView"]>[0]) {
        this.camera?.setView(options);
    }

    public setRotation(degreesInRadians: number) {
        this.setView({ orientation: { heading: degreesInRadians } });
    }

    public lockRotation(shouldLockRotation: boolean) {
        if (!this.scene) {
            return;
        }
        this.scene.screenSpaceCameraController.enableRotate = !shouldLockRotation;
    }

    /**
     * Make the camera track a specific entity
     * @param cesiumEntity - cesium entity( billboard, polygon...) to track
     * @param options - track entity options
     */
    public trackEntity(cesiumEntity?: Entity, options?: { flyTo: boolean; flyToDuration?: number; altitude?: number }) {
        if (!this.viewer) {
            return Promise.resolve();
        }

        const flyTo = (options && options.flyTo) || false;

        this.viewer.trackedEntity = undefined;

        return new Promise<void>((resolve) => {
            if (flyTo) {
                const flyToDuration = (options && options.flyToDuration) || 1;
                // eslint-disable-next-line no-magic-numbers
                const altitude = (options && options.altitude) || 10000;

                // Calc entity flyTo position and wanted altitude
                const position = cesiumEntity?.position?.getValue(JulianDate.now());
                if (!position) {
                    resolve();

                    return;
                }
                const cartographicPosition = Cartographic.fromCartesian(position);
                const zoomAmount = altitude - cartographicPosition.height;
                cartographicPosition.height = altitude;
                const flyToPosition = Cartesian3.fromRadians(
                    cartographicPosition.longitude,
                    cartographicPosition.latitude,
                    cartographicPosition.height
                );

                this.cameraFlyTo({
                    duration: flyToDuration,
                    destination: flyToPosition,
                    complete: () => {
                        if (!this.viewer) {
                            resolve();

                            return;
                        }
                        this.viewer.trackedEntity = cesiumEntity;
                        setTimeout(() => {
                            if (zoomAmount > 0) {
                                this.camera?.zoomOut(zoomAmount);
                            } else {
                                this.camera?.zoomIn(zoomAmount);
                            }
                        }, 0);
                        resolve();
                    },
                });
            } else {
                if (this.viewer) {
                    this.viewer.trackedEntity = cesiumEntity;
                }
                resolve();
            }
        });
    }

    public untrackEntity() {
        this.trackEntity();
    }
}
